import { formatBpAsPercent, type NormsView } from '@pms/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { request } from '../../sync/api.js';

/**
 * Нормативы: сколько замеса уходит на плиту и какой выход считается нормой.
 *
 * По этим числам списывается сырьё и подсвечиваются отклонения, поэтому правка
 * требует причины и попадает в историю. Обычная причина — переточили пресс-форму,
 * плита стала легче или тяжелее. Через полгода вопрос «почему в марте расход
 * был другой» должен иметь ответ, а не догадку.
 */

export function NormsScreen(): JSX.Element {
  const [data, setData] = useState<NormsView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      setData(await request<NormsView>('/api/norms', { method: 'GET' }, 20_000));
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось загрузить нормативы');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (key: string, action: () => Promise<string>): Promise<void> => {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      const done = await action();
      await load();
      setNotice(done);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не получилось');
    } finally {
      setBusy(null);
    }
  };

  const editProduct = async (
    productSizeId: string,
    name: string,
    currentG: number,
  ): Promise<void> => {
    const raw = window.prompt(
      `Расход замеса на одну плиту «${name}», граммы:`,
      String(currentG),
    );
    if (raw === null) return;
    const grams = Number(raw.replace(',', '.').trim());
    if (!Number.isInteger(grams) || grams <= 0) {
      setError('Расход — целое число граммов');
      return;
    }

    const reason = window.prompt(
      'Почему меняется норматив?\nНапример: переточили пресс-форму, плита стала легче',
      '',
    );
    if (reason === null || reason.trim().length < 3) return;

    await act(productSizeId, async () => {
      await request<NormsView>(
        '/api/norms/product',
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ productSizeId, mixNormPerPieceG: grams, reason: reason.trim() }),
        },
        20_000,
      );
      return 'Норматив изменён, прежнее значение осталось в истории';
    });
  };

  const editYield = async (
    machineId: string,
    name: string,
    currentBp: number | null,
  ): Promise<void> => {
    const raw = window.prompt(
      `Норма выхода на агрегате «${name}», проценты:`,
      currentBp === null ? '' : String(currentBp / 100),
    );
    if (raw === null) return;
    const percent = Number(raw.replace(',', '.').trim());
    if (!Number.isFinite(percent) || percent <= 0 || percent > 100) {
      setError('Выход — число от 0 до 100 процентов');
      return;
    }

    const reason = window.prompt('Почему меняется норма выхода?', '');
    if (reason === null || reason.trim().length < 3) return;

    await act(machineId, async () => {
      await request<NormsView>(
        '/api/norms/machine-yield',
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            machineId,
            yieldBp: Math.round(percent * 100),
            reason: reason.trim(),
          }),
        },
        20_000,
      );
      return 'Норма выхода изменена';
    });
  };

  const editSiteNorm = async (
    siteId: string,
    title: string,
    name: string,
    currentBp: number,
  ): Promise<void> => {
    const raw = window.prompt(`${title} на участке «${name}», проценты:`, String(currentBp / 100));
    if (raw === null) return;
    const percent = Number(raw.replace(',', '.').trim());
    if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
      setError('Потери — число от 0 до 100 процентов');
      return;
    }

    const reason = window.prompt('Почему меняется норматив?', '');
    if (reason === null || reason.trim().length < 3) return;

    await act(siteId, async () => {
      await request<NormsView>(
        '/api/norms/site',
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            siteId,
            key: 'REWORK_LOSS_BP',
            valueBp: Math.round(percent * 100),
            reason: reason.trim(),
          }),
        },
        20_000,
      );
      return 'Норматив изменён';
    });
  };

  return (
    <div className="min-h-screen px-4 py-6">
      <header className="mb-6 flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Нормативы</h1>
        <Link to="/" className="rounded-xl bg-muted px-4 py-3 text-base font-semibold">
          Назад
        </Link>
      </header>

      {error && <p className="mb-4 rounded-xl bg-rose-50 text-rose-800 ring-1 ring-rose-200 px-4 py-3 text-base">{error}</p>}
      {notice && (
        <p className="mb-4 rounded-xl bg-muted px-4 py-3 text-base text-body">{notice}</p>
      )}

      <h2 className="mb-1 text-lg font-semibold">Расход замеса на плиту</h2>
      <p className="mb-3 text-base text-dim">
        По этим числам замес списывается от выработки прессовщика — сам он расход не вводит.
      </p>
      <div className="mb-8 flex flex-col gap-3">
        {(data?.products ?? []).map((product) => (
          <article key={product.productSizeId} className="rounded-xl bg-card shadow-card p-4">
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-lg font-semibold">{product.name}</p>
              <p className="text-lg tabular-nums">{product.mixNormPerPieceG} г</p>
            </div>
            <button
              type="button"
              disabled={busy === product.productSizeId}
              onClick={() =>
                void editProduct(product.productSizeId, product.name, product.mixNormPerPieceG)
              }
              className="mt-3 rounded-xl bg-muted px-4 py-3 text-base font-semibold disabled:opacity-50"
            >
              Изменить
            </button>
          </article>
        ))}
      </div>

      <h2 className="mb-1 text-lg font-semibold">Нормы выхода переделов</h2>
      <p className="mb-3 text-base text-dim">
        Сколько получается из переработанного сырья. Норма у каждого агрегата своя:
        гильотина режет без отходов, дробилка рядом даёт меньше.
      </p>
      <div className="mb-8 flex flex-col gap-3">
        {(data?.machines ?? []).map((machine) => (
          <article key={machine.machineId} className="rounded-xl bg-card shadow-card p-4">
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-lg font-semibold">{machine.name}</p>
              <p className="text-lg tabular-nums">
                {machine.yieldBp === null ? 'не задана' : formatBpAsPercent(machine.yieldBp)}
              </p>
            </div>
            <p className="text-base text-dim">
              {machine.siteName}
              {machine.conversion ? ` · ${machine.conversion}` : ''}
            </p>
            <button
              type="button"
              disabled={busy === machine.machineId}
              onClick={() => void editYield(machine.machineId, machine.name, machine.yieldBp)}
              className="mt-3 rounded-xl bg-muted px-4 py-3 text-base font-semibold disabled:opacity-50"
            >
              {machine.yieldBp === null ? 'Задать' : 'Изменить'}
            </button>
          </article>
        ))}
      </div>

      <h2 className="mb-1 text-lg font-semibold">Потери при переплавке брака</h2>
      <p className="mb-3 text-base text-dim">
        Брак нельзя отгрузить или выбросить — его переплавляют на месте. Возвращается
        не всё, и потерянная доля списывается сверх нормы на годную плитку.
      </p>
      <div className="mb-8 flex flex-col gap-3">
        {(data?.sites ?? []).map((site) => (
          <article key={site.siteId} className="rounded-xl bg-card shadow-card p-4">
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-lg font-semibold">{site.name}</p>
              <p className="text-lg tabular-nums">{formatBpAsPercent(site.valueBp)}</p>
            </div>
            <button
              type="button"
              disabled={busy === site.siteId}
              onClick={() => void editSiteNorm(site.siteId, site.title, site.name, site.valueBp)}
              className="mt-3 rounded-xl bg-muted px-4 py-3 text-base font-semibold disabled:opacity-50"
            >
              Изменить
            </button>
          </article>
        ))}
      </div>

      <h2 className="mb-1 text-lg font-semibold">История правок</h2>
      <p className="mb-3 text-base text-dim">
        Нужна, чтобы объяснить расход за прошлые периоды.
      </p>
      <div className="flex flex-col gap-3">
        {(data?.history ?? []).length === 0 && (
          <p className="rounded-xl bg-card shadow-card p-4 text-base text-dim">
            Нормативы ещё не меняли.
          </p>
        )}
        {(data?.history ?? []).map((change) => (
          <article key={change.id} className="rounded-xl bg-canvas p-4">
            <p className="text-base font-semibold">
              {change.targetName}:{' '}
              {change.target === 'PRODUCT_MIX_NORM'
                ? `${change.fromValue} г → ${change.toValue} г`
                : `${formatBpAsPercent(change.fromValue)} → ${formatBpAsPercent(change.toValue)}`}
            </p>
            <p className="text-base text-dim">{change.reason}</p>
            <p className="mt-1 text-base text-faint">
              {change.byUserName}, {new Date(change.at).toLocaleString('ru-RU')}
            </p>
          </article>
        ))}
      </div>
    </div>
  );
}
